import { mixinMethods } from '@/main'
import { API } from 'aws-amplify'

/**
 * HERA-8696: Ensures all default custom lists exist for the current tenant.
 *
 * Instead of hardcoding list definitions, this calls the existing backend
 * 'create-by-template-id' mutation which reads the list structure and options
 * from the template tenant's DynamoDB data. The backend is idempotent —
 * it skips any list types that already exist for the tenant.
 *
 * @param {Object} store - Vuex store instance
 * @param {Array} customLists - Currently loaded custom lists
 * @returns {boolean} true if lists may have been created (caller should reload)
 */
export async function ensureDefaultCustomLists(store, customLists) {
  try {
    // If vehicle-expiration already exists, nothing to do
    const hasVehicleExpiration = customLists.some(list => list.type === 'vehicle-expiration')
    if (hasVehicleExpiration) return false

    // Check if tenant has vehicle management feature
    if (!store.getters.hasVehicleManagement) return false

    const tenant = store.state.userInfo?.tenant
    if (!tenant?.group) return false

    // Get the template tenant ID — needed to know where to copy lists from
    const templateTenantId = tenant.templateTenantId
    if (!templateTenantId) {
      console.warn('Tenant has no templateTenantId, cannot auto-create custom lists')
      return false
    }

    // Fetch the template tenant's group
    const templateTenantResult = await mixinMethods.api(getTemplateTenant, { id: templateTenantId })
    const templateGroupId = templateTenantResult?.data?.getTenant?.group
    if (!templateGroupId) {
      console.warn('Could not resolve template tenant group')
      return false
    }

    // Call the existing backend API to create missing custom lists from template
    // The backend is idempotent — it checks each type and skips existing ones
    await API.post('heraPublicApi', '/executeMutation', {
      body: {
        type: 'custom-list',
        mutation: 'create-by-template-id',
        group: tenant.group,
        tenantId: tenant.id,
        owner: store.state.userInfo.cognitoSub,
        templateGroupId: templateGroupId,
        templateId: templateTenantId
      }
    })

    return true
  } catch (e) {
    // Fail silently — the feature will just show empty state until next login
    console.error('[HERA-8696] Failed to auto-create custom lists from template:', e)
    return false
  }
}

// Minimal query to get template tenant's group
const getTemplateTenant = /* GraphQL */ `
  query GetTenant($id: ID!) {
    getTenant(id: $id) {
      id
      group
    }
  }
`
